import { Tag } from "antd";
import { useAppSelector } from "../../../../../../app/store";
import { TicketDetails, TicketWorkflow } from "../../../../../../features/superAdmin/ticketService/ticket/ticket.type";
import {
  convertMinutesToSeconds,
  convertSecondsToHoursShort,
  formatDate,
  formatTime,
  getSecondsDiff,
} from "../../../../../../lib/time";
import { cn } from "../../../../../../utils/string";
import {
  ActivityRoleType,
  getRoleStatus,
} from "../../../../../../utils/ticket";

const WorkflowActivityItem = ({
  ticketDetails,
  workFlow,
}: {
  ticketDetails: TicketDetails;
  workFlow: TicketWorkflow;
}) => {
  const user_id = useAppSelector((state) => state?.auth?.user?.id);
  const currentFlow = ticketDetails?.currentWorkflow as TicketWorkflow;

  const {
    id,
    updated_at,
    role_type,
    employee_id,
    employee,
    visibility_duration_minutes: vMinutes,
    status,
    start_time,
    bypass,
    note,
  } = workFlow;

  const indexOfCurrent =
    ticketDetails?.workflows?.findIndex((i: any) => i?.id === id) || 0;

  const indexOfCurrentActive =
    ticketDetails?.workflows?.findIndex(
      (i: any) => i?.id === currentFlow?.id,
    ) || 0;

  const visibilityInSecond = convertMinutesToSeconds(vMinutes);
  const diffInSeconds = getSecondsDiff(
    status === "pending" ? new Date().toISOString() : updated_at,
    start_time,
  );
  const isAssignedUser = employee_id === user_id;
  const isDone = status === "done";

  const employeeName = employee?.officialInfo?.name || "Unknown";
  const isBreached = diffInSeconds >= visibilityInSecond;

  const renderTimestamp = () => {
    if (
      indexOfCurrentActive !== -1 &&
      indexOfCurrent !== -1 &&
      indexOfCurrentActive < indexOfCurrent
    ) {
      return null;
    }
    if (currentFlow?.employee?.id === workFlow?.employee?.id) {
      return (
        <div className="flex items-center">
          <span className="text-[#5E718D]">
            {formatTime(start_time, "h:mm:ss")} - {formatDate(start_time)}
            &nbsp;
          </span>
          <span className="text-body font-medium">
            - Base SLA:&nbsp;
            {convertSecondsToHoursShort(vMinutes)}
          </span>
        </div>
      );
    }

    return isDone || (bypass && isBreached) ? (
      <div className="space-y-[5px] mt-[5px]">
        <div className="flex items-center">
          <span className="text-[#5E718D]">
            {formatTime(start_time, "h:mm:ss")} - {formatDate(start_time)}
            &nbsp;
          </span>
          <span className="text-body font-medium">
            - Base SLA:&nbsp;
            {convertSecondsToHoursShort(vMinutes)}
          </span>
        </div>
        <div className="text-gray-500 flex items-center gap-2 flex-wrap">
          {bypass && isBreached ? (
            <span className="text-body font-medium text-nowrap">
              Bypass SLA:&nbsp;
              {convertSecondsToHoursShort(vMinutes)} -
            </span>
          ) : (
            <span className="text-body font-medium text-nowrap">
              Completion SLA:&nbsp;
              {convertSecondsToHoursShort(diffInSeconds)} -
            </span>
          )}
          <span className="text-body font-medium text-nowrap">
            SLA Status:&nbsp;
            {isBreached ? (
              <span className="text-xs inline-block px-2 py-[4.5px] rounded-full bg-[#FFF5F4] text-[#FF3838]">
                Breached
              </span>
            ) : (
              <span className="text-xs inline-block px-2 py-[4.5px] rounded-full bg-status-dark-bg text-body">
                Normal
              </span>
            )}
          </span>
        </div>
        {note && (
          <span className="text-sm text-[#5E718D]">
            <span className="text-body">Comment:&nbsp;</span>
            {note}
          </span>
        )}
      </div>
    ) : null;
  };

  const fixedStatus = getRoleStatus(role_type as ActivityRoleType, {
    isBypassed:  isBreached && bypass,
    isDone,
    status,
  });

  return (
    <div>
      <div
        className={cn(
          `font-semibold text-base`,
          isDone || isAssignedUser ? "text-body" : "text-display-dark",
        )}
      >
        Ticket {fixedStatus} {isBreached && bypass ? "from" : "by"}{" "}
        {employeeName}
        {isAssignedUser ? (
          <Tag className="ml-2 bg-status-dark-bg text-body border-none">
            You
          </Tag>
        ) : null}
      </div>
      <div className="text-gray-500">{renderTimestamp()}</div>
    </div>
  );
};

export default WorkflowActivityItem;